import { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { SiteReportContent } from "./SiteReportContent";
import { usePrintLogging, useToggleList } from "./hooks";

import { Alert } from "react-migration/components/Alert";
import { AssessmentTab } from "./AssessmentTab";
import { Button } from "react-migration/components/Button";
import { Checkbox } from "react-migration/components/Checkbox";
import { FileInput } from "react-migration/components/FileInput";
import { Label } from "react-migration/components/Label";
import { Nullable } from "src/js/types/Nullable";
import clsx from "clsx";
import { feature } from "@turf/helpers";
import { siteOptionsSchema } from "./siteOptions";
import { useSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { useSiteReportLogo } from "./useSiteReportLogo";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { DemographicsExtraControls } from "../map/Multilayer/layer_types/DemographicsLayerType/DemographicsExtraControls";
import { LayerScopeControls } from "./components/LayerScopeControls";
import { SiteReportDemographicsProvider } from "../map/Multilayer/layer_types/DemographicsLayerType/DemographicsProvider";
import { useAssessmentStateManager } from "./useAssessmentStateManager";

interface SiteReportRouteContainerProps {
  siteId: string;
  isShared?: boolean;
}

/**
 * Component to allow user to configure site report & show preview (akin to Chrome Print Preview).
 *
 * @param {string} props.siteId - The selected feature for which the site report is generated.
 * @param {boolean} props.isShared - Whether the site is shared.
 * @example
 * <SiteReportRouteContainer siteId={string} isShared={boolean} />
 */
export function SiteReportRouteContainer({ siteId, isShared = false }: Readonly<SiteReportRouteContainerProps>) {
  const { t } = useTranslation();

  const { site } = useSiteById(siteId, { isShared });

  const siteAsSelectionFeature = useMemo<Nullable<SelectionFeature>>(() => {
    if (!site?.geometry || !site?._id) return null;

    return { type: SelectionType.SITE, id: site._id, feature: feature(site?.geometry) };
  }, [site]);

  const {
    assessmentState,
    toggleAssessmentState,
    activeAssessmentLayers,
    reportLoadedHandle,
    checkboxState,
    assessmentTabs,
    reportLoadingState,
  } = useAssessmentStateManager({
    siteId,
    supportsSectionIds: true,
    featureFlags: {
      nearbyTab: true,
    },
  });

  const assessmentTabKeys = assessmentTabs.map((tab) => tab.key);

  const { isReadyToDownload, getTabLoadingState } = reportLoadingState;

  const [siteOptions, toggleSiteOptions] = useToggleList(Object.keys(siteOptionsSchema));
  const { logoFile, handleLogoUpload, error } = useSiteReportLogo(siteId);

  const enhancedAssessmentTabs = useMemo(() => {
    if (!siteAsSelectionFeature) return assessmentTabs;

    return assessmentTabs.map((tab) => {
      if (tab.key === "compsAndLocal") {
        return {
          ...tab,
          groups: tab.groups.map((group) => {
            if (group.id === "demographics") {
              return {
                ...group,
                extraControls: <DemographicsExtraControls siteId={siteId} selectionFeature={siteAsSelectionFeature} />,
              };
            }
            if (group.id === "comparables") {
              return {
                ...group,
                extraControls: (
                  <div className="flex flex-col gap-2">
                    <LayerScopeControls siteId={siteId} layerId="COMPARABLES" selection={siteAsSelectionFeature} />
                  </div>
                ),
              };
            }
            return group;
          }),
        };
      }

      if (tab.key === "planningHistory") {
        return {
          ...tab,
          groups: tab.groups.map((group) => ({
            ...group,
            extraControls: (
              <LayerScopeControls
                siteId={siteId}
                layerId="PLANNING_APPLICATIONS_ASSESSMENT"
                selection={siteAsSelectionFeature}
              />
            ),
          })),
        };
      }

      return tab;
    });
  }, [assessmentTabs, siteId, siteAsSelectionFeature]);

  const { printDownloadClicked, printDownloaded } = usePrintLogging(siteId, activeAssessmentLayers, isShared);
  useEffect(() => {
    function handleAfterPrint() {
      printDownloaded();
    }

    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [printDownloaded, siteId, isShared]);

  const handleDownloadClick = useCallback(() => {
    printDownloadClicked();
    window.print();
  }, [printDownloadClicked]);

  if (!siteAsSelectionFeature) return null;

  return (
    <SiteReportDemographicsProvider selection={siteAsSelectionFeature}>
      <div
        className={clsx(
          "h-[calc(100dvh-var(--navbar-mobile-height))] md:h-[calc(100dvh-var(--navbar-height))]",
          "w-full",
          "flex",
          "gap-4",
          "p-4",
          "print:p-0"
        )}
        data-testid="site-report-container"
      >
        <div className="w-full print:hidden md:w-1/4">
          <div className="h-full p-6 rounded-lg bg-blue-grey-100">
            <div className="flex flex-col h-full gap-8 pb-8 overflow-x-hidden overflow-y-auto">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col w-full gap-2">
                  <Label showOptionalText>{t("sites.card.site_report.upload_logo")}</Label>
                  <FileInput onSelectFile={handleLogoUpload} value={logoFile ?? undefined} />
                  {error && <Alert.Error>{error}</Alert.Error>}
                </div>
                {!!assessmentTabKeys.length && (
                  <div className="flex flex-col gap-2">
                    <FormHeading>Assessment</FormHeading>
                    <div className="flex flex-col gap-2">
                      {enhancedAssessmentTabs.map((tab) => (
                        <AssessmentTab
                          key={tab.key}
                          tab={tab}
                          loadingState={getTabLoadingState(tab, checkboxState.getCheckboxState)}
                          checkedState={assessmentState[tab.key]}
                          handleToggleTab={toggleAssessmentState}
                          getCheckboxState={checkboxState.getCheckboxState}
                          toggleCheckbox={checkboxState.toggleCheckbox}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <FormHeading>Management</FormHeading>
                  <div className="flex flex-col gap-2">
                    {Object.entries(siteOptionsSchema).map(([key, { displayName }]) => (
                      <div className="flex items-center gap-2" key={key}>
                        <Checkbox
                          id={key}
                          checked={siteOptions.includes(key)}
                          onCheckedChange={() => toggleSiteOptions(key)}
                        />
                        <label htmlFor={key} className="text-sm select-none text-blue-grey-950">
                          {displayName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Button
                disabled={!isReadyToDownload}
                leadIcon={isReadyToDownload ? "lt-download-line" : "lt-loader-4-line"}
                onClick={handleDownloadClick}
                leadIconAnimation={isReadyToDownload ? undefined : "spin"}
              >
                {!isReadyToDownload ? t("site_report.loading") : t("site_report.save_as_pdf")}
              </Button>
            </div>
          </div>
        </div>
        <div className={clsx("flex", "w-3/4", "h-full", "print:w-full")}>
          <div
            className={clsx(
              "w-full",
              "overflow-y-scroll",
              "bg-white",
              "p-2",
              "border-8",
              "rounded-lg",
              "border-blue-grey-400",
              "print:rounded-none",
              "print:border-0",
              "print:overflow-visible",
              "print:p-1"
            )}
          >
            <SiteReportContent
              activeAssessmentLayers={activeAssessmentLayers}
              siteOptions={siteOptions}
              selection={siteAsSelectionFeature}
              onReportLoaded={reportLoadedHandle}
              isShared={isShared}
            />
          </div>
        </div>
      </div>
    </SiteReportDemographicsProvider>
  );
}

export function FormHeading({ children }: PropsWithChildren) {
  return <h2 className="text-base font-semibold text-blue-grey-950">{children}</h2>;
}
